import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import SummaryCards from './components/SummaryCards';
import LogFilters from './components/LogFilters';
import LogTable from './components/LogTable';
import LogDetailModal from './components/LogDetailModal';
import { fetchLogs as fetchLogsRequest, deleteLogs } from '../services/logs.service';

export type LogCategory =
  | 'http_error'
  | 'exception'
  | 'slow_request'
  | 'third_party'
  | 'db_error'
  | 'app_error'
  | 'security';

export type LogLevel = 'error' | 'warn' | 'info';

export interface AppLogDoc {
  _id: string;
  category: LogCategory;
  level: LogLevel;
  message: string;
  userId?: string;
  orgId?: string;
  userEmail?: string;
  ip?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface AppLogFilters {
  category?: LogCategory;
  level?: LogLevel;
  orgId?: string;
  from?: string;
  to?: string;
}

interface PagedResult {
  data: AppLogDoc[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export default function DiagnosticDashboard() {
  const [filters, setFilters] = useState<AppLogFilters>({});
  const [logs, setLogs] = useState<AppLogDoc[]>([]);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AppLogDoc | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const fetchLogs = useCallback(
    async (currentFilters: AppLogFilters, page: number, pageSize: number) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (currentFilters.category) params.set('category', currentFilters.category);
        if (currentFilters.level) params.set('level', currentFilters.level);
        if (currentFilters.orgId) params.set('orgId', currentFilters.orgId);
        if (currentFilters.from) params.set('from', currentFilters.from);
        if (currentFilters.to) params.set('to', currentFilters.to);
        params.set('page', String(page + 1)); // API is 1-based
        params.set('limit', String(pageSize));

        const result = (await fetchLogsRequest(params.toString())) as PagedResult;
        setLogs(result.data);
        setTotal(result.total);
      } catch (err) {
        console.error('[DiagnosticDashboard] fetchLogs error:', err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchLogs(filters, paginationModel.page, paginationModel.pageSize);
  }, [filters, paginationModel, fetchLogs]);

  const handleFiltersChange = (newFilters: AppLogFilters) => {
    setFilters(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSelectedIds([]);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    try {
      await deleteLogs(selectedIds);
      setSelectedIds([]);
      fetchLogs(filters, paginationModel.page, paginationModel.pageSize);
    } catch (err) {
      console.error('[DiagnosticDashboard] delete error:', err);
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Typography variant="h5" fontWeight={700} mb={3}>
          Diagnostics
        </Typography>
        <Stack spacing={3}>
          <SummaryCards />
          <LogFilters filters={filters} onChange={handleFiltersChange} />
          <LogTable
            rows={logs}
            total={total}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            onRowClick={setSelected}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onDeleteSelected={handleDeleteSelected}
          />
        </Stack>
        <LogDetailModal
          log={selected}
          open={selected !== null}
          onClose={() => setSelected(null)}
        />
      </Box>
  );
}
